import Type from "typebox";

import {
  ApiKeyIdSchema,
  HostIdSchema,
  MembershipIdSchema,
  ProjectIdSchema,
  TenantIdSchema,
  UserIdSchema,
} from "./resources.js";

export const ROLE_CODES = ["owner", "admin", "operator", "viewer"] as const;

export const RoleSchema = Type.Union([
  Type.Literal("owner"),
  Type.Literal("admin"),
  Type.Literal("operator"),
  Type.Literal("viewer"),
]);

export type Role = Type.Static<typeof RoleSchema>;

export const PERMISSION_CODES = [
  "system:read",
  "installation:manage",
  "team:read",
  "team:manage",
  "api_keys:read",
  "api_keys:manage",
  "audit:read",
  "projects:read",
  "projects:manage",
  "projects:delete",
  "hosts:read",
  "hosts:manage",
  "deployments:read",
  "deployments:create",
  "deployments:cancel",
  "deployments:rollback",
  "jobs:read",
  "jobs:cancel",
  "logs:read",
  "config:read",
  "config:write",
  "secrets:write",
  "domains:read",
  "domains:write",
  "databases:read",
  "databases:operate",
  "databases:backup",
  "databases:restore",
  "databases:delete",
] as const;

export const PermissionSchema = Type.Union([
  Type.Literal("system:read"),
  Type.Literal("installation:manage"),
  Type.Literal("team:read"),
  Type.Literal("team:manage"),
  Type.Literal("api_keys:read"),
  Type.Literal("api_keys:manage"),
  Type.Literal("audit:read"),
  Type.Literal("projects:read"),
  Type.Literal("projects:manage"),
  Type.Literal("projects:delete"),
  Type.Literal("hosts:read"),
  Type.Literal("hosts:manage"),
  Type.Literal("deployments:read"),
  Type.Literal("deployments:create"),
  Type.Literal("deployments:cancel"),
  Type.Literal("deployments:rollback"),
  Type.Literal("jobs:read"),
  Type.Literal("jobs:cancel"),
  Type.Literal("logs:read"),
  Type.Literal("config:read"),
  Type.Literal("config:write"),
  Type.Literal("secrets:write"),
  Type.Literal("domains:read"),
  Type.Literal("domains:write"),
  Type.Literal("databases:read"),
  Type.Literal("databases:operate"),
  Type.Literal("databases:backup"),
  Type.Literal("databases:restore"),
  Type.Literal("databases:delete"),
]);

export type Permission = Type.Static<typeof PermissionSchema>;

export const ROLE_PERMISSIONS = {
  viewer: [
    "system:read",
    "projects:read",
    "hosts:read",
    "deployments:read",
    "jobs:read",
    "logs:read",
    "config:read",
    "domains:read",
    "databases:read",
  ],
  operator: [
    "system:read",
    "projects:read",
    "hosts:read",
    "deployments:read",
    "deployments:create",
    "deployments:cancel",
    "jobs:read",
    "jobs:cancel",
    "logs:read",
    "config:read",
    "config:write",
    "secrets:write",
    "domains:read",
    "databases:read",
    "databases:operate",
    "databases:backup",
  ],
  admin: [
    "system:read",
    "team:read",
    "team:manage",
    "api_keys:read",
    "api_keys:manage",
    "audit:read",
    "projects:read",
    "projects:manage",
    "projects:delete",
    "hosts:read",
    "hosts:manage",
    "deployments:read",
    "deployments:create",
    "deployments:cancel",
    "deployments:rollback",
    "jobs:read",
    "jobs:cancel",
    "logs:read",
    "config:read",
    "config:write",
    "secrets:write",
    "domains:read",
    "domains:write",
    "databases:read",
    "databases:operate",
    "databases:backup",
    "databases:restore",
    "databases:delete",
  ],
  owner: [...PERMISSION_CODES],
} as const satisfies Record<Role, readonly Permission[]>;

export const ROLE_DEFINITIONS = {
  owner: {
    label: "Sahip",
    description: "Tüm ayarları ve sahipliği yönetir",
  },
  admin: {
    label: "Yönetici",
    description: "Ekip ve sistem ayarlarını yönetir",
  },
  operator: {
    label: "Operatör",
    description: "İzin verilen operasyonları yürütür",
  },
  viewer: {
    label: "Görüntüleyici",
    description: "Yalnızca yetkili bilgileri görüntüler",
  },
} as const satisfies Record<Role, { readonly label: string; readonly description: string }>;

export const API_KEY_SCOPE_CODES = [
  "system:read",
  "audit:read",
  "projects:read",
  "hosts:read",
  "deployments:read",
  "deployments:create",
  "deployments:cancel",
  "deployments:rollback",
  "jobs:read",
  "jobs:cancel",
  "logs:read",
  "config:read",
  "config:write",
  "secrets:write",
  "domains:read",
  "domains:write",
  "databases:read",
  "databases:operate",
  "databases:backup",
  "databases:restore",
] as const;

export const ApiKeyScopeSchema = Type.Union([
  Type.Literal("system:read"),
  Type.Literal("audit:read"),
  Type.Literal("projects:read"),
  Type.Literal("hosts:read"),
  Type.Literal("deployments:read"),
  Type.Literal("deployments:create"),
  Type.Literal("deployments:cancel"),
  Type.Literal("deployments:rollback"),
  Type.Literal("jobs:read"),
  Type.Literal("jobs:cancel"),
  Type.Literal("logs:read"),
  Type.Literal("config:read"),
  Type.Literal("config:write"),
  Type.Literal("secrets:write"),
  Type.Literal("domains:read"),
  Type.Literal("domains:write"),
  Type.Literal("databases:read"),
  Type.Literal("databases:operate"),
  Type.Literal("databases:backup"),
  Type.Literal("databases:restore"),
]);

export type ApiKeyScope = Type.Static<typeof ApiKeyScopeSchema>;

export const RestrictedResourceBoundarySchema = Type.Object(
  {
    mode: Type.Literal("restricted"),
    projectIds: Type.Array(ProjectIdSchema, { maxItems: 500, uniqueItems: true }),
    hostIds: Type.Array(HostIdSchema, { maxItems: 500, uniqueItems: true }),
  },
  { additionalProperties: false },
);

export const AllResourceBoundarySchema = Type.Object(
  {
    mode: Type.Literal("all_resources"),
  },
  { additionalProperties: false },
);

export const ResourceBoundarySchema = Type.Union([
  RestrictedResourceBoundarySchema,
  AllResourceBoundarySchema,
]);

export type ResourceBoundary = Type.Static<typeof ResourceBoundarySchema>;

export const UserPrincipalSchema = Type.Object(
  {
    kind: Type.Literal("user"),
    tenantId: TenantIdSchema,
    userId: UserIdSchema,
    membershipId: MembershipIdSchema,
    role: RoleSchema,
    permissions: Type.Array(PermissionSchema, { uniqueItems: true }),
  },
  { additionalProperties: false },
);

export const ApiKeyPrincipalSchema = Type.Object(
  {
    kind: Type.Literal("api_key"),
    tenantId: TenantIdSchema,
    apiKeyId: ApiKeyIdSchema,
    scopes: Type.Array(ApiKeyScopeSchema, { minItems: 1, uniqueItems: true }),
    resourceBoundary: ResourceBoundarySchema,
  },
  { additionalProperties: false },
);

export const SystemPrincipalSchema = Type.Object(
  {
    kind: Type.Literal("system"),
    component: Type.String({ minLength: 1, maxLength: 80, pattern: "^[a-z][a-z0-9_-]*$" }),
  },
  { additionalProperties: false },
);

export const AnonymousPrincipalSchema = Type.Object(
  {
    kind: Type.Literal("anonymous"),
  },
  { additionalProperties: false },
);

export const PrincipalSchema = Type.Union([
  UserPrincipalSchema,
  ApiKeyPrincipalSchema,
  SystemPrincipalSchema,
  AnonymousPrincipalSchema,
]);

export type UserPrincipal = Type.Static<typeof UserPrincipalSchema>;
export type ApiKeyPrincipal = Type.Static<typeof ApiKeyPrincipalSchema>;
export type SystemPrincipal = Type.Static<typeof SystemPrincipalSchema>;
export type AnonymousPrincipal = Type.Static<typeof AnonymousPrincipalSchema>;
export type Principal = Type.Static<typeof PrincipalSchema>;
